import type { UseQueryResult } from '@tanstack/react-query'
import type { ComponentProps, ReactNode } from 'react'

import { Callout, LoadingIndicator } from '@/components/base'

/**
 * A region that waits on one query: what stands in its place while the answer
 * is on its way, what it says when the request failed, and the content once
 * there is data. An Internal region reading a single endpoint draws this, so
 * a failure reads the same on all of them.
 *
 * Data wins over a failure, which is why the test is `data` and not
 * `isSuccess`: a screen that polls keeps what it has when one refetch does
 * not answer, instead of blanking a filled region on every network hiccup.
 */
export function QueryGate<T>({
  query,
  loading = 'block',
  error,
  children,
}: {
  /** Narrower than `UseQueryResult` on purpose: these two fields are the
   *  whole contract, and a caller can stand one in for a demo. */
  query: Pick<UseQueryResult<T>, 'data' | 'isError'>
  /** Which stand-in the region is the right size for. */
  loading?: ComponentProps<typeof LoadingIndicator>['layout']
  /** What the reader is told when there is nothing and the request failed. */
  error: string
  children: (data: T) => ReactNode
}) {
  if (query.data !== undefined) return <>{children(query.data)}</>
  if (query.isError) return <Callout tone="error">{error}</Callout>
  return <LoadingIndicator layout={loading} />
}
